import {FC, useEffect, useRef, useState} from 'react'
import {useHistory} from 'react-router-dom'
import {useLayout} from '../../../_metronic/layout/core'
import {getEngagements} from '../../services/engagementService'
import {counterpartyLabel, Engagement, formatMoney, statusColor, statusLabel} from './engagements'

// WP-X6 — "My engagements" for both roles (as client / as expert), against the deployed #43 API.
const MyEngagementsPage: FC = () => {
  const history = useHistory()
  const {config, setLayout} = useLayout()
  const restoreRef = useRef(config)

  const [role, setRole] = useState<'client' | 'expert'>('client')
  const [rows, setRows] = useState<Engagement[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    restoreRef.current = config
    setLayout({
      ...config,
      header: {...config.header, display: true},
      aside: {...config.aside, display: true},
      toolbar: {...config.toolbar, display: false},
      footer: {...config.footer, display: true},
    })
    return () => setLayout(restoreRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        setLoading(true)
        setError(false)
        const data = await getEngagements(role)
        if (!active) return
        // The list endpoint returns an array (or a paginated {results}).
        setRows(Array.isArray(data) ? data : data?.results ?? [])
      } catch (err) {
        if (active) {
          setError(true)
          setRows([])
        }
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => {
      active = false
    }
  }, [role])

  return (
    <div className='my-engagements-page'>
      <h1 className='mb-6'>My Engagements</h1>

      <ul className='nav nav-tabs nav-line-tabs fs-6 mb-6'>
        <li className='nav-item'>
          <button
            type='button'
            className={`nav-link ${role === 'client' ? 'active' : ''}`}
            onClick={() => setRole('client')}
          >
            As client
          </button>
        </li>
        <li className='nav-item'>
          <button
            type='button'
            className={`nav-link ${role === 'expert' ? 'active' : ''}`}
            onClick={() => setRole('expert')}
          >
            As expert
          </button>
        </li>
      </ul>

      {error ? (
        <div className='text-center py-10 text-muted'>
          Couldn’t load your engagements right now.
        </div>
      ) : !loading && rows.length === 0 ? (
        <div className='text-center py-10'>
          {role === 'client'
            ? 'You haven’t requested any engagements yet.'
            : 'No one has engaged you as an expert yet.'}
        </div>
      ) : (
        <table className='admin-community__table'>
          <thead>
            <tr>
              <th>{role === 'client' ? 'Expert' : 'Client'}</th>
              <th>Scope</th>
              <th>Amount</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id}>
                <td>{counterpartyLabel(e)}</td>
                <td className='text-truncate' style={{maxWidth: 320}}>
                  {e.scope || '—'}
                </td>
                <td>{formatMoney(e.agreedAmount, e.currency)}</td>
                <td>
                  <span
                    className='badge'
                    style={{backgroundColor: statusColor(e.status), color: '#fff'}}
                  >
                    {statusLabel(e.status)}
                  </span>
                </td>
                <td>
                  <button
                    type='button'
                    className='nb-link-underline-500'
                    onClick={() => history.push(`/engagements/${e.id}`)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export default MyEngagementsPage
